/**
 * Синхронното състояние на локалния резерв („edge") — режим, конфигурация и
 * адрес на касата — за код ИЗВЪН React дървото: axios интерсепторът в
 * `api/client.ts` чете режима ПРИ ВСЯКА заявка, `apiBase.ts` смята базовия
 * адрес, `useStationWebSocket` строи WS адреса.
 *
 * Модулът НЯМА импорти нарочно: `api/client.ts` → `lib/apiBase.ts` → тук е
 * най-долният слой и всичко над него (edgeMode.ts, Redux `edgeSlice`) го
 * ползва без цикъл. Единственият ПИСАЧ е `edgeMode.ts` — той държи Redux
 * `edgeSlice` (за UI-я) и този модул (за интерсепторите) в синхрон.
 * Договорът: docs/edge.md.
 */

export type EdgeMode = "cloud" | "local";

/** `GET /configuration/edge` в camelCase (виж `api/edge.ts`). */
export interface EdgeConfig {
  enabled: boolean;
  /** LAN IP на главната каса от presence-а на master-а; null = не е обявена. */
  ip: string | null;
  port: number;
  masterClientId: string | null;
  /** Режимът, в който ОБЛАКЪТ смята ресторанта (fence) — информативен. */
  cloudMode: EdgeMode;
  edgeApi: number;
}

/** Версията на договора, която този FE говори (`edge_api`). */
export const EDGE_API_VERSION = 1;

/** Кешът за старт без облак — `localStorage["edge_config"]` (docs/edge.md). */
export const EDGE_CONFIG_STORAGE_KEY = "edge_config";
/** Билетите за касата — `localStorage["edge_ticket"]`. */
export const EDGE_TICKET_STORAGE_KEY = "edge_ticket";
/** Запомненият режим за студен старт — `localStorage["edge_mode"]` (`edgeModeCache.ts`). */
export const EDGE_MODE_STORAGE_KEY = "edge_mode";
/** Последната позната каса — `localStorage["edge_last_known"]` (`edgeLastKnown.ts`);
 * ЕДИНСТВЕНИЯТ ключ на резерва, който изходът НЕ трие (§15.4). */
export const EDGE_LAST_KNOWN_STORAGE_KEY = "edge_last_known";
/** „Този браузър е на самата каса" — `localStorage["edge_this_till"]` = нейният
 * `client_id` (`edgeLoopback.ts`). Като `edge_last_known` и той НЕ се трие при
 * изход: това е свойство на машината, не на сесия или човек. */
export const EDGE_THIS_TILL_STORAGE_KEY = "edge_this_till";

/** Касата, когато браузърът е на същия компютър. IP литерал, не `localhost`:
 * не зависи от hosts файла, а Chrome смята и двете за надежден адрес — HTTP
 * заявка от HTTPS страница не е mixed content, за разлика от публичен IP. */
export const EDGE_LOOPBACK_HOST = "127.0.0.1";

let currentMode: EdgeMode = "cloud";
let currentConfig: EdgeConfig | null = null;

export const getEdgeMode = (): EdgeMode => currentMode;
export const isEdgeLocalMode = (): boolean => currentMode === "local";
export function setEdgeModeState(mode: EdgeMode) {
  currentMode = mode;
}

export const getEdgeConfigState = (): EdgeConfig | null => currentConfig;
export function setEdgeConfigState(config: EdgeConfig | null) {
  currentConfig = config;
}

/**
 * Базовият адрес на касата — `http://{ip}:{port}/edge` (§1 на договора).
 * Под него пътищата са същите като на BE (`/bills`, `/menu/`, `/user/info`),
 * а собствените endpoint-и на edge-а са `/health`, `/status`, `/mode/enter`,
 * `/mirror`. null = резервът е изключен или касата не е обявила адрес.
 * Винаги IP, никога домейн: Chrome освобождава от Local Network Access
 * проверката само частен IP литерал.
 *
 * Изключението е браузърът на САМАТА каса (`isThisDeviceTheTill`): той ходи
 * през `127.0.0.1` — смяна на IP-то, публичен адрес от доставчика или
 * firewall не го засягат. Всички пътища към касата минават оттук, затова
 * замяната е само на това място.
 */
export function getEdgeBaseUrl(): string | null {
  if (!currentConfig?.enabled) return null;
  if (isThisDeviceTheTill()) return getEdgeLoopbackBaseUrl();
  return getEdgeLanBaseUrl();
}

/** Адресът, който касата е ОБЯВИЛА в мрежата — без замяната с loopback. */
export function getEdgeLanBaseUrl(): string | null {
  if (!currentConfig?.enabled || !currentConfig.ip) return null;
  return `http://${currentConfig.ip}:${currentConfig.port}/edge`;
}

/** Касата на този компютър — за сондата, която проверява дали това е тя. */
export function getEdgeLoopbackBaseUrl(): string | null {
  if (!currentConfig?.enabled) return null;
  return `http://${EDGE_LOOPBACK_HOST}:${currentConfig.port}/edge`;
}

// ─── Този браузър е на самата каса ─────────────────────────────────────────

/** undefined = още не е четено от storage (четем лениво: модулът няма init). */
let thisTillClientId: string | null | undefined;

export function getThisTillClientId(): string | null {
  if (thisTillClientId === undefined) {
    thisTillClientId = edgeStorageGet(EDGE_THIS_TILL_STORAGE_KEY);
  }
  return thisTillClientId;
}

/** Пише само `edgeLoopback.ts` — след сонда, която е видяла касата през
 * `127.0.0.1` със същия `client_id` като главната (или вече не я вижда там). */
export function setThisTillClientId(clientId: string | null) {
  thisTillClientId = clientId;
  if (clientId) edgeStorageSet(EDGE_THIS_TILL_STORAGE_KEY, clientId);
  else edgeStorageRemove(EDGE_THIS_TILL_STORAGE_KEY);
}

/**
 * Запомнената каса на ТОЗИ компютър е и главната на обекта. Без
 * `masterClientId` (старт без облак от `edge_last_known`) вярваме на
 * запомненото — сондата така или иначе проверява касата преди локален режим.
 * Сменена главна каса → обратно към обявения адрес, без да трием нищо.
 */
export function isThisDeviceTheTill(): boolean {
  const clientId = getThisTillClientId();
  if (!clientId) return false;
  const master = currentConfig?.masterClientId;
  return !master || master === clientId;
}

/**
 * Частен адрес по разбирането на Chrome — само към такъв HTTPS страницата
 * пуска HTTP заявка (Local Network Access). Публичен адрес, който някои
 * доставчици раздават направо на машината, се спира като mixed content още
 * преди заявката да тръгне — дори да е на същия компютър. Списъкът е същият
 * като в портала на FiscalAPI (`app/edge/config.py`).
 */
export function isPrivateLanAddress(ip: string | null | undefined): boolean {
  if (!ip || !/^\d{1,3}(\.\d{1,3}){3}$/.test(ip)) return false;
  const octets = ip.split(".").map(Number);
  if (octets.some((octet) => octet > 255)) return false;
  const [a, b] = octets;
  return (
    a === 10 ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168) ||
    (a === 100 && b >= 64 && b <= 127) ||
    (a === 169 && b === 254)
  );
}

// ─── Storage (както lib/auth.ts: всеки достъп в try/catch) ────────────────
export const edgeStorageGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
export const edgeStorageSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage недостъпен — просто не помним */
  }
};
export const edgeStorageRemove = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage недостъпен */
  }
};

// ─── Сигнали от заявките към облака ────────────────────────────────────────
// axios интерсепторът (и WS-ът на мониторите) не могат да импортират
// edgeMode.ts без цикъл, затова само СИГНАЛИЗИРАТ; edgeMode.ts се абонира
// при старт и решава какво значи това за режима.

/**
 * След колко мълчание заявката към облака се обявява за БАВНА. 1,5 s е под
 * прага, на който човек започва да натиска пак — лентата казва „бавно"
 * преди сервитьорът да е посегнал втори път към бутона, а сондите тръгват
 * веднага, вместо да чакат първата заявка да умре след 8 s.
 */
export const SLOW_AFTER_MS = 1_500;

/**
 * Какъв провал е видяла заявката:
 *  • `down`     няма никакъв отговор (таймаут, DNS, отказана връзка);
 *  • `app_down` отговор има, но приложението е долу (5xx) — жива мрежа,
 *               мъртъв облак. Най-често НАШ деплой, затова чакането е
 *               по-дълго (`lib/edge/edgeMode.ts`).
 */
export type CloudErrorKind = "down" | "app_down";

type Listener<T> = (payload: T) => void;

function signal<T>() {
  const listeners = new Set<Listener<T>>();
  return {
    on(listener: Listener<T>) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    emit(payload: T) {
      for (const listener of listeners) listener(payload);
    },
  };
}

const cloudNetworkError = signal<CloudErrorKind>();
const cloudSlow = signal<void>();
const cloudFence = signal<void>();
const cloudResponded = signal<void>();

export const onCloudNetworkError = (listener: Listener<CloudErrorKind>) =>
  cloudNetworkError.on(listener);

/** Провалена заявка към облака. Без аргумент = „нищо не отговори" — така го
 * викат WS-ът на мониторите и login-ите, които нямат HTTP статус. */
export function emitCloudNetworkError(kind: CloudErrorKind = "down") {
  cloudNetworkError.emit(kind);
}

export const onCloudSlow = (listener: Listener<void>) => cloudSlow.on(listener);

/** Заявка към облака мълчи вече `SLOW_AFTER_MS` — още не е провал. */
export function emitCloudSlow() {
  cloudSlow.emit(undefined);
}

export const onCloudFence = (listener: Listener<void>) => cloudFence.on(listener);

/**
 * Облакът е отговорил 423: САМ е решил, че обектът е в локален режим (fence-ът
 * на BE-то). Това не е грешка за показване — то е заповед. Терминалът няма
 * какво да проверява и влиза срещу касата веднага.
 */
export function emitCloudFence() {
  cloudFence.emit(undefined);
}

export const onCloudResponded = (listener: Listener<void>) =>
  cloudResponded.on(listener);

// ─── „Облакът отговори поне веднъж в това зареждане" ───────────────────────
// Разделя студения старт от вече работеща сесия (§13.1 на договора):
// докато облакът не е отговорил (какъвто и да е HTTP отговор от НЕГО, вкл.
// 4xx, или успешна сонда), първата мрежова грешка + жива каса превключва
// ВЕДНАГА в локален режим; отговори ли веднъж, важи хистерезисът от три сонди
// — за да няма премигване при единичен timeout. Флагът е само за това
// зареждане на страницата (не се пази в storage). Пише го интерсепторът в
// api/client.ts.
let cloudRespondedThisLoad = false;

/**
 * Облакът отговори — и това е ДОКАЗАТЕЛСТВО, а не просто „нещо отговори".
 *
 * Разликата е носеща. Прихващащ прокси (хотел, ISP редирект „неплатена
 * сметка", ingress без backend) отговаря 200 или 404 с HTML на СВОЯ вход.
 * Дотук всеки такъв отговор минаваше оттук, сваляше `cloudFailures` на нула и
 * връщаше лентата на „online" — тоест обект зад такова нещо НЕ МОЖЕШЕ да
 * замръзне, докато сервитьорът тропа по екрана, а всяка поръчка отиваше в
 * HTML страницата. Измерено: 60 s непрекъсната работа дават 4 сонди вместо 30
 * и нито едно `mode/enter`.
 *
 * Затова викащият казва дали отговорът е НАШ (`api/client.ts`:
 * `isCloudOwnResponse` — JSON тяло). Не е ли, тук не се случва нищо: решава
 * сондата `GET {cloud}/health`, която чете тялото и знае разликата.
 */
export function markCloudResponded(fromCloud: boolean = true) {
  if (!fromCloud) return;
  cloudRespondedThisLoad = true;
  // И жив сигнал, не само флаг: всеки отговор от облака е свидетелство, че
  // връзката се е върнала — състоянието „бавно"/„съмнение" пада още тук,
  // без да чака следващата сонда.
  cloudResponded.emit(undefined);
}

export const hasCloudRespondedThisLoad = (): boolean => cloudRespondedThisLoad;
